import { useInfiniteQuery, useMutation } from '@tanstack/react-query';

import {
  type IdentifierAssociationsReportRequest,
  type IdentifierAssociationsRequest,
  IdentifierAssociationsRequester,
  type IdentifierAssociationsResponse,
} from '@cobliteam/api-lib';

import { Environment } from '@cobliteam/react-environments';

const env = Environment.getInstance().getEnvironment();
const identifierAssociationsRequester = new IdentifierAssociationsRequester({ host: env.apiHost });

const QUERY_KEY = 'IDENTIFIER_ASSOCIATIONS';

const useIdentifierAssociationsRows = (request: IdentifierAssociationsRequest) => {
  return useInfiniteQuery<IdentifierAssociationsResponse>({
    initialPageParam: 0,
    queryKey: [QUERY_KEY, 'ROWS', JSON.stringify(request)],
    queryFn: ({ pageParam = 0 }) =>
      identifierAssociationsRequester.getIdentifierAssociations({
        ...request,
        page: pageParam as number,
        size: 100,
      }),
    staleTime: 1000 * 60 * 30, // 30 minutes
    getNextPageParam,
  });
};

const useRequestIdentifierAssociationsReport = () =>
  useMutation<string, unknown, { request: IdentifierAssociationsReportRequest }>({
    mutationFn: (variables) => identifierAssociationsRequester.requestIdentifierAssociationsReport(variables.request),
  });

const getNextPageParam = (lastPage: IdentifierAssociationsResponse, allPages: IdentifierAssociationsResponse[]) => {
  const loadedItems = allPages
    .flat()
    .map((response) => response.rows.length)
    .reduce((acc, a) => acc + a);

  if (lastPage.count === loadedItems) return undefined;
  return allPages.flat().length;
};

export default {
  useIdentifierAssociationsRows,
  useRequestIdentifierAssociationsReport,
};
